import React, { useEffect, useMemo, useState, useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import * as commondata from "../../Common";
import DonutChart from "../charts/DonutChart";
import { useDeepCompareMemoize } from "use-deep-compare-effect";
import ProgressReportSkeleton from "../skeleton/ProgressReportSkeleton";

const colors = {
  success: "#28A745",
  secondary: "#6C757D",
  primary: "#007BFF",
  danger: "#DC3545",
  warning: "#FFC107",
  info: "#17A2B8",
  light: "#F8F9FA",
  dark: "#343A40",
};

const ProgressReport = forwardRef(({
  attribute_set_key,
  customer_tattle_uuid,
  childBoardDataChanged,
  childLoading,
  progressFilterChanged,
  clearSegmentFun
}, ref) => {

  const [progressData, setProgressData] = useState(null);
  const [activeSegment, setActiveSegment] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);

  const chartRef1 = useRef();
  const chartRef2 = useRef();
  const chartRef3 = useRef();

  const memoizedProgressData = useDeepCompareMemoize(progressData);

  // Clear segment function that can be called from outside
  const clearSegment = useCallback(() => {
    setActiveSegment(null);
    setSelectedSegment(null);
    progressFilterChanged({ status: [] });

    // Also clear the chart selection directly via ref
    if (chartRef1.current?.chart) {
      chartRef1.current.chart.series[0].data.forEach((point) => {
        point.select(false);
      });
    }
    if (chartRef2.current?.chart) {
      chartRef2.current.chart.series[0].data.forEach((point) => {
        point.select(false);
      });
    }
    if (chartRef3.current?.chart) {
      chartRef3.current.chart.series[0].data.forEach((point) => {
        point.select(false);
      });
    }
  }, [progressFilterChanged]);

  // Expose clearSegment function to parent component
  useImperativeHandle(ref, () => ({
    clearSegment: () => {
      clearSegment();
    }
  }));

  useEffect(() => {
    setTimeout(() => {
      clearSegmentFun(clearSegment);
    }, 1000);
  }, [clearSegmentFun, clearSegment]);

  const handleSegmentClick = useCallback((key, filter) => {
    setActiveSegment(currentActive => {
      if (currentActive === key) {
        // Clear if clicking the same segment
        progressFilterChanged({ status: [] });
        setSelectedSegment(null);
        return null;
      } else {
        // Set new active segment
        progressFilterChanged({ status: filter || [] });
        setSelectedSegment(key);
        return key;
      }
    });
  }, [progressFilterChanged]);

  // Memoize constant data
  const common = useMemo(() => commondata.getCommonData(), []);

  const getChartData = useCallback((progressData) => {
    if (!progressData?.report) return [];

    const report = progressData.report;

    return Object.entries(report).map(([key, value]) => {
      const y = value.er || 0;
      const isSelected = selectedSegment === key;

      return {
        name: value.name,
        y,
        color: colors[value.color] ?? "#999999",
        pointFormat: `ERs: ${y}`,
        showLegendVal: true,
        legendSuffix: y == 0 ? "0": y,
        selected: isSelected,
        onClick: () => handleSegmentClick(key, value.filter || [])
      };
    });
  }, [handleSegmentClick, selectedSegment]);

  console.log(progressData, 'opopppoopopppo')
  
const getSingleProgressChartData = useCallback(
  (data) => {
    if (!data || typeof data !== "object") return [];

    return Object.entries(data).map(([id, item]) => {
      const effort = parseFloat(item.efforts) || 0;
      const days = Math.floor(effort / 8);
      const hours = effort % 8;

      return {
        name: item.name,
        y: effort / 8,
        color: colors[item.color] || "#999999",
        showLegendVal: true,
        legendSuffix: `${Math.ceil(days)} Days ${hours} Hours`, // ✅ spelling corrected
        pointFormat: `<b>${Math.ceil(days)} Days ${hours} Hours</b>`,
        selected: selectedSegment === id,
        onClick: () => handleSegmentClick(id, item.filter),
      };
    });
  },
  [handleSegmentClick, selectedSegment, progressData?.total_qa_effort,
    progressData?.total_dev_efforts]
);


  // Memoize chart data calculations with safe access
  const chartData = useMemo(() => getChartData(progressData), [memoizedProgressData]);

  const devChartData = useMemo(() => {
    if (!progressData || (Array.isArray(progressData) && progressData.length === 0)) {
      return [];
    }

    return getSingleProgressChartData(
      progressData?.Dev || {}
    );
  }, [memoizedProgressData]);

  const qaChartData = useMemo(() => {
    if (!progressData || (Array.isArray(progressData) && progressData.length === 0)) {
    return [];
    }

    return getSingleProgressChartData(
      progressData?.Qa
    );
  }, [memoizedProgressData]);
  const generateCenterText = (total, closed) => {
    return `<span style="font-size: 30px; font-weight: bold;"> ${total === 0 ? 0 : Math.round((closed / total) * 100)}%</span><br/><span style=" font-size: 18px; ">Completed</span>`;
  }

  // Memoized async fetch
  const getProgressReport = async () => {
    try {
      const res = await fetch(`${common.apiUrl}reports/progress-report`, {
        method: "POST",
        body: JSON.stringify({
          app_token: common.app_token,
          attribute_set_key,
          customer_tattle_uuid,
          target_attribute_set_key: "er",
        }),
      });
      const data = await res.json();

      // Handle empty data case
      if (data?.status === "success" && Array.isArray(data.data) && data.data.length === 0) {
        setProgressData({
          total_ers: 0,
          overall_closed_efforts: 0,
          overall_efforts: 0,
          closed_dev_efforts: 0,
          total_dev_efforts: 0,
          closed_qa_efforts: 0,
          total_qa_effort: 0,
          report: {} // Empty report object instead of null
        });
      } else {
        setProgressData(data?.data || null);
      }

    } catch (error) {
      console.error("Error fetching progress report:", error);
      setProgressData(null);
    }
  };

  useEffect(() => {
    if (childLoading) {
      return;
    }
    setTimeout(() => {
      getProgressReport();
    }, 100);
  }, [childBoardDataChanged]);

    const formatDays = useCallback(
    (closed, total) =>
      `${Math.floor(closed / 8) || 0} Days  ${closed % 8} Hours / ${Math.floor(total / 8) || 0} Days ${total % 8} Hours`,
    []
  );

  // Check if we have valid data to display
  const hasValidData = progressData && progressData.total_ers !== undefined;

  return (
    <div className="analytics-cards">
      {
        !hasValidData ? (
          <ProgressReportSkeleton />
        ) : (
          <div className="row px-0 mx-0">

            {/* Overall Progress */}
            <div className="card-box">
              <div className="card-text-row d-flex justify-content-between">
                <div>
                  <h3>Overall Efforts</h3>
                  <p className="small-text">
                    {formatDays(
                      progressData?.overall_closed_efforts || 0,
                      progressData?.overall_efforts || 0
                    )}
                  </p>
                </div>
                <p className="small-text total-ers text-end">
                  Total ERs
                  <br />
                  <span>{progressData?.total_ers ?? "0"}</span>
                </p>
              </div>

              <div className="er-graph">
                <div className="chart-center d-flex align-items-center justify-content-between m-0">
                  <div className="chart-img">
                    {chartData.length > 0 ? (
                      <DonutChart
                        ref={chartRef1}
                        data={chartData}
                        name="ERs"
                        showLegend={true}
                        centerTxt={generateCenterText(
                          progressData.total_ers || 0, 
                          progressData.total_closed_ers || 0
                        )}
                      />
                    ) : (
                      <p>No data available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Development Progress */}
            <div className="card-box">
              <h3>Development Progress</h3>
              <p className="small-text">
                {formatDays(
                  progressData?.closed_dev_efforts || 0,
                  progressData?.total_dev_efforts || 0
                )}
              </p>
              <div className="chart-center">
                {chartData.length > 0 ? (
                  <DonutChart
                    ref={chartRef2}
                    data={devChartData}
                    name="Dev Efforts"
                    centerTxt={generateCenterText(
                      progressData?.total_dev_efforts || 0, 
                      progressData?.closed_dev_efforts || 0
                    )}
                  />
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>

            {/* QA Progress */}
            <div className="card-box">
              <h3>QA Progress</h3>
              <p className="small-text">
                {formatDays(
                  progressData?.closed_qa_efforts || 0,
                  progressData?.total_qa_effort || 0
                )}
              </p>
              <div className="chart-center">
                {chartData.length > 0 ? (
                  <DonutChart
                    ref={chartRef3}
                    data={qaChartData}
                    name="QA Efforts"
                    centerTxt={generateCenterText(
                      progressData?.total_qa_effort || 0, 
                      progressData?.closed_qa_efforts || 0
                    )}
                  />
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>

          </div>
        )
      }
    </div>
  );
});

ProgressReport.displayName = 'ProgressReport';

export default React.memo(ProgressReport);